import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { ProjektService } from '../projekt.service';
import { SpsPruefungApiService } from '../sps-pruefung-api.service';
import { OfflineDbService } from './offline-db.service';
import { OfflineAktionErgebnis, OfflineDownloadService } from './offline-download.service';
import { OfflineSyncService } from './offline-sync.service';
import { NetworkStatusService } from './network-status.service';

import {
  OFFLINE_PROJEKT_SCHEMA_VERSION,
  OfflinePaket,
  OfflineProjektEintrag,
  OfflineProjektStatus,
  ProjektDownloadFortschritt,
  ProjektOfflineManifest,
  ProjektOfflineZustand,
  SpsOfflinePaket
} from '../../Models/offline.model';

/**
 * Das Projekt als Offline-Einheit: herunterladen, aktualisieren, entfernen –
 * und der Zustand je Projekt für Projektansicht und Projektübersicht.
 *
 * Ablauf eines Downloads:
 *
 * 1. Erreichbarkeit prüfen (echte Anfrage, nicht `navigator.onLine`).
 * 2. Alles Ausstehende dieses Projekts synchronisieren. Bleibt etwas liegen
 *    (Konflikt, Fehler, keine Verbindung), wird NICHT heruntergeladen – der
 *    Download würde sonst eigene, noch nicht übertragene Arbeit überschreiben.
 * 3. Manifest holen, dann je Liste das Listenpaket, dann das SPS-Paket.
 *    Jeder Teil zählt den Fortschritt hoch.
 * 4. Erst wenn ALLE Teile im Speicher sind, schreibt EINE Dexie-Transaktion
 *    alles und zuletzt den Projektkopf. Bricht etwas ab (Browser zu, Speicher
 *    voll), bleibt der bisherige Bestand vollständig stehen.
 *
 * Ein Projekt gilt erst mit Projektkopf als offline verfügbar. Einzelne
 * Listen, die aus der Zeit vor der Projektfunktion lokal liegen, bleiben
 * lesbar und werden beim ersten Projekt-Download in das Projekt übernommen.
 */
@Injectable({ providedIn: 'root' })
export class ProjektOfflineService {

  private readonly status$ = new BehaviorSubject<OfflineProjektStatus[]>([]);

  /** Laufende Downloads mit Fortschritt. */
  private readonly laufend = new Map<number, ProjektDownloadFortschritt>();

  /** Letzter Download-Fehler je Projekt – bleibt bis zum nächsten Versuch sichtbar. */
  private readonly fehler = new Map<number, string>();

  constructor(
    private projektApi: ProjektService,
    private spsApi: SpsPruefungApiService,
    private listen: OfflineDownloadService,
    private db: OfflineDbService,
    private sync: OfflineSyncService,
    private network: NetworkStatusService
  ) {
    // Jede Änderung der Warteschlange schlägt auf den Projektzustand durch.
    this.sync.syncStatus$.subscribe(() => void this.aktualisiereStatus());
  }

  get offlineStatus$(): Observable<OfflineProjektStatus[]> {
    return this.status$.asObservable();
  }

  /** Zustand eines Projekts als Strom – auch für Projekte, die (noch) nicht lokal liegen. */
  statusFuer$(projektId: number): Observable<OfflineProjektStatus> {
    return this.status$.pipe(
      map(alle => alle.find(s => s.projektId === projektId) ?? this.leererStatus(projektId))
    );
  }

  statusVon(projektId: number): OfflineProjektStatus {
    return this.status$.value.find(s => s.projektId === projektId) ?? this.leererStatus(projektId);
  }

  laeuftGerade(projektId: number): boolean {
    return this.laufend.has(projektId);
  }

  // ===================================================================
  // HERUNTERLADEN / AKTUALISIEREN
  // ===================================================================

  /**
   * Lädt das komplette Projekt auf das Gerät – oder bringt einen vorhandenen
   * Bestand auf den aktuellen Serverstand. Beides läuft gleich: erst
   * synchronisieren, dann laden, dann in einer Transaktion schreiben.
   */
  async herunterladen(projektId: number): Promise<OfflineAktionErgebnis> {
    if (!(await this.db.istVerfuegbar())) {
      return {
        ok: false,
        grund: 'fehler',
        meldung:
          'Dieser Browser stellt keinen lokalen Speicher bereit. '
          + 'Im privaten Modus ist die Offline-Nutzung nicht möglich.'
      };
    }

    if (this.laufend.has(projektId)) {
      return { ok: false, grund: 'fehler', meldung: 'Dieses Projekt wird gerade heruntergeladen.' };
    }

    this.fehler.delete(projektId);
    this.setzeFortschritt(projektId, 'Verbindung wird geprüft …', 0, 1);

    try {
      // 1. Ist der Server wirklich da?
      if (!(await this.network.pruefeErreichbarkeit())) {
        return {
          ok: false,
          grund: 'fehler',
          meldung: 'Der Server ist nicht erreichbar. Zum Herunterladen wird eine Verbindung gebraucht.'
        };
      }

      // 2. Ausstehendes zuerst hochschicken – nichts darf überschrieben werden.
      const sperre = await this.pruefeOffeneAenderungen(projektId);

      if (sperre) {
        return sperre;
      }

      // 3. Manifest.
      this.setzeFortschritt(projektId, 'Projektdaten werden geladen …', 0, 1);

      const manifest = await firstValueFrom(this.projektApi.getOfflineManifest(projektId));

      if (!manifest) {
        return { ok: false, grund: 'fehler', meldung: 'Der Server hat keine Projektdaten geliefert.' };
      }

      if (manifest.schemaVersion !== OFFLINE_PROJEKT_SCHEMA_VERSION) {
        return {
          ok: false,
          grund: 'fehler',
          meldung:
            'Die Anwendung ist nicht auf demselben Stand wie der Server. '
            + 'Bitte die Seite neu laden und es erneut versuchen.'
        };
      }

      const hatSps = manifest.spsListen.length > 0 || manifest.spsSignale > 0;
      const gesamt = 1 + manifest.listen.length + (hatSps ? 1 : 0) + 1;
      let erledigt = 1;

      // 4. Je Liste das Listenpaket.
      const listenPakete: OfflinePaket[] = [];

      for (const liste of manifest.listen) {
        this.setzeFortschritt(projektId, `Liste „${liste.name}" wird geladen …`, erledigt, gesamt);
        listenPakete.push(await this.listen.ladePaket(liste.id));
        erledigt++;
      }

      // 5. Das SPS-Paket.
      let spsPaket: SpsOfflinePaket | null = null;

      if (hatSps) {
        this.setzeFortschritt(projektId, 'SPS-Listen und Signale werden geladen …', erledigt, gesamt);
        spsPaket = await firstValueFrom(this.spsApi.getOfflinePaket(projektId));
        erledigt++;

        if (spsPaket && spsPaket.schemaVersion !== OFFLINE_PROJEKT_SCHEMA_VERSION) {
          return {
            ok: false,
            grund: 'fehler',
            meldung: 'Die Anwendung ist nicht auf demselben Stand wie der Server. Bitte die Seite neu laden.'
          };
        }
      }

      // 6. Alles in einer Transaktion schreiben.
      this.setzeFortschritt(projektId, 'Daten werden auf dem Gerät gespeichert …', erledigt, gesamt);

      await this.schreibeProjekt(manifest, listenPakete, spsPaket);

      this.setzeFortschritt(projektId, 'Fertig', gesamt, gesamt);

      return { ok: true, standUtc: manifest.erzeugtAmUtc };
    } catch (fehler: any) {
      const text = this.listen.fehlertext(fehler);
      this.fehler.set(projektId, text);

      return { ok: false, grund: 'fehler', meldung: text };
    } finally {
      this.laufend.delete(projektId);
      await this.aktualisiereStatus();
    }
  }

  /** Dasselbe wie `herunterladen` – der Name sagt, was der Benutzer meint. */
  aktualisiere(projektId: number): Promise<OfflineAktionErgebnis> {
    return this.herunterladen(projektId);
  }

  /**
   * Synchronisiert und prüft danach, ob für dieses Projekt noch etwas offen
   * ist. Liefert das Hindernis oder `null`, wenn der Weg frei ist.
   */
  private async pruefeOffeneAenderungen(projektId: number): Promise<OfflineAktionErgebnis | null> {
    const listenIds = await this.lokaleListenIds(projektId);

    if (await this.zaehleOffen(projektId, listenIds) > 0) {
      this.setzeFortschritt(projektId, 'Ausstehende Änderungen werden übertragen …', 0, 1);
      await this.sync.synchronisiere('manuell');
    }

    const konflikte = (await this.db.ladeKonflikte())
      .filter(k => this.db.gehoertZuProjekt(k, projektId, listenIds)).length;

    if (konflikte > 0) {
      return {
        ok: false,
        grund: 'konflikt',
        anzahl: konflikte,
        meldung:
          konflikte === 1
            ? 'Ein Synchronisierungskonflikt ist offen. Bitte zuerst entscheiden, welche Fassung gelten soll.'
            : `${konflikte} Synchronisierungskonflikte sind offen. Bitte zuerst entscheiden, welche Fassung gelten soll.`
      };
    }

    const ausstehend = (await this.db.ladeWarteschlange())
      .filter(e => this.db.gehoertZuProjekt(e, projektId, listenIds)).length;

    if (ausstehend > 0) {
      return {
        ok: false,
        grund: 'ausstehend',
        anzahl: ausstehend,
        meldung:
          ausstehend === 1
            ? 'Eine Änderung wurde noch nicht synchronisiert. Die Offline-Daten werden erst danach aktualisiert.'
            : `${ausstehend} Änderungen wurden noch nicht synchronisiert. Die Offline-Daten werden erst danach aktualisiert.`
      };
    }

    return null;
  }

  /**
   * Schreibt Manifest, Listenpakete und SPS-Paket – alles oder nichts.
   *
   * Listen, die laut Manifest nicht mehr zum Projekt gehören (gelöscht oder
   * umgehängt), werden lokal entfernt; Ausstehendes dazu gibt es an dieser
   * Stelle nicht mehr, siehe `pruefeOffeneAenderungen`.
   */
  private async schreibeProjekt(
    manifest: ProjektOfflineManifest,
    listenPakete: OfflinePaket[],
    spsPaket: SpsOfflinePaket | null
  ): Promise<void> {
    const db = this.db.db;
    const projektId = manifest.projekt.id;
    const vorhanden = await this.db.ladeProjekt(projektId);

    const kopf: OfflineProjektEintrag = {
      id: projektId,
      name: manifest.projekt.name,
      projektNummer: manifest.projekt.projektNummer,
      customerName: manifest.projekt.customerName ?? null,
      downloadedAtUtc: manifest.erzeugtAmUtc,
      lastSyncAtUtc: vorhanden?.lastSyncAtUtc ?? null,
      schemaVersion: manifest.schemaVersion,
      canEdit: manifest.canEdit,
      projekt: manifest.projekt,
      listenIds: manifest.listen.map(l => l.id),
      spsListenIds: manifest.spsListen.map(l => l.id),
      spsSignaleCount: spsPaket?.signale.length ?? manifest.spsSignale
    };

    await db.transaction(
      'rw',
      [
        db.projekte, db.listen, db.messstellen, db.details, db.kategorien, db.qrZuordnungen,
        db.spsListen, db.spsEintraege, db.spsSignale
      ],
      async () => {
        // ── Messstellenlisten ──

        const alteListen = await db.listen.where('projektId').equals(projektId).toArray();
        const neueIds = new Set(kopf.listenIds);

        for (const alt of alteListen) {
          if (!neueIds.has(alt.id)) {
            await this.listen.loescheListenbestand(alt.id);
          }
        }

        for (const paket of listenPakete) {
          await this.listen.schreibePaket(paket);
        }

        // ── SPS ──

        await db.spsListen.where('projektId').equals(projektId).delete();
        await db.spsEintraege.where('projektId').equals(projektId).delete();
        await db.spsSignale.where('projektId').equals(projektId).delete();

        if (spsPaket) {
          await db.spsListen.bulkPut(
            spsPaket.listen.map(l => ({ id: l.id, projektId, data: l }))
          );

          await db.spsEintraege.bulkPut(
            spsPaket.eintraege.map(e => ({
              id: e.eintrag.id,
              listeId: e.listeId,
              projektId,
              data: e.eintrag
            }))
          );

          await db.spsSignale.bulkPut(
            spsPaket.signale.map(s => ({ id: s.id, projektId, data: s }))
          );
        }

        // ── Zuletzt der Projektkopf: erst jetzt gilt das Projekt als offline verfügbar ──
        await db.projekte.put(kopf);
      }
    );
  }

  // ===================================================================
  // ENTFERNEN
  // ===================================================================

  /**
   * Entfernt den lokalen Bestand eines Projekts – aber nur, wenn nichts mehr
   * aussteht. Die Warteschlange ist der einzige Ort, an dem eine offline
   * erfasste Prüfung existiert, solange sie nicht beim Server ist.
   * Andere Projekte bleiben unberührt.
   */
  async entferne(projektId: number): Promise<OfflineAktionErgebnis> {
    const listenIds = await this.lokaleListenIds(projektId);
    const offen = await this.zaehleOffen(projektId, listenIds);

    if (offen > 0) {
      const konflikte = (await this.db.ladeKonflikte())
        .filter(k => this.db.gehoertZuProjekt(k, projektId, listenIds)).length;

      return {
        ok: false,
        grund: konflikte > 0 ? 'konflikt' : 'ausstehend',
        anzahl: offen,
        meldung:
          offen === 1
            ? 'Eine Änderung wurde noch nicht synchronisiert. Die Offline-Daten können deshalb nicht entfernt werden.'
            : `${offen} Änderungen wurden noch nicht synchronisiert. Die Offline-Daten können deshalb nicht entfernt werden.`
      };
    }

    const db = this.db.db;

    await db.transaction(
      'rw',
      [
        db.projekte, db.listen, db.messstellen, db.details, db.qrZuordnungen,
        db.spsListen, db.spsEintraege, db.spsSignale
      ],
      async () => {
        for (const listeId of listenIds) {
          await this.listen.loescheListenbestand(listeId);
        }

        await db.spsListen.where('projektId').equals(projektId).delete();
        await db.spsEintraege.where('projektId').equals(projektId).delete();
        await db.spsSignale.where('projektId').equals(projektId).delete();
        await db.qrZuordnungen.where('projektId').equals(projektId).delete();
        await db.projekte.delete(projektId);
      }
    );

    this.fehler.delete(projektId);
    await this.aktualisiereStatus();
    await this.listen.aktualisiereStatus();

    return { ok: true, standUtc: new Date().toISOString() };
  }

  // ===================================================================
  // STATUS
  // ===================================================================

  /** Ermittelt den Zustand aller lokal vorhandenen Projekte neu. */
  async aktualisiereStatus(): Promise<OfflineProjektStatus[]> {
    if (!(await this.db.istVerfuegbar())) {
      this.status$.next([]);
      return [];
    }

    const projekte = await this.db.ladeProjekte();
    const listen = await this.db.ladeListen();
    const warteschlange = await this.db.ladeWarteschlange();
    const konflikte = await this.db.ladeKonflikte();

    const ids = new Set<number>([
      ...projekte.map(p => p.id),
      ...this.laufend.keys(),
      ...this.fehler.keys()
    ]);

    const status: OfflineProjektStatus[] = [];

    for (const projektId of ids) {
      const projekt = projekte.find(p => p.id === projektId);
      const listenIds = new Set<number>([
        ...(projekt?.listenIds ?? []),
        ...listen.filter(l => l.projektId === projektId).map(l => l.id)
      ]);

      const eigene = warteschlange.filter(e => this.db.gehoertZuProjekt(e, projektId, listenIds));
      const fehlgeschlagen = eigene.filter(e => e.endgueltigFehlgeschlagen).length;
      const ausstehend = eigene.length - fehlgeschlagen;
      const offeneKonflikte = konflikte.filter(k => this.db.gehoertZuProjekt(k, projektId, listenIds)).length;
      const fortschritt = this.laufend.get(projektId) ?? null;
      const fehlertext = this.fehler.get(projektId) ?? eigene.find(e => e.endgueltigFehlgeschlagen)?.letzterFehler ?? null;

      status.push({
        projektId,
        zustand: this.zustand(!!projekt, !!fortschritt, ausstehend, offeneKonflikte, fehlgeschlagen, !!this.fehler.get(projektId)),
        verfuegbar: !!projekt,
        downloadedAtUtc: projekt?.downloadedAtUtc ?? null,
        lastSyncAtUtc: projekt?.lastSyncAtUtc ?? null,
        ausstehend,
        konflikte: offeneKonflikte,
        fehlgeschlagen,
        schemaVeraltet: !!projekt && projekt.schemaVersion !== OFFLINE_PROJEKT_SCHEMA_VERSION,
        fortschritt,
        fehlertext,
        listen: projekt?.listenIds.length ?? listenIds.size,
        spsListen: projekt?.spsListenIds.length ?? 0
      });
    }

    this.status$.next(status);

    return status;
  }

  private zustand(
    verfuegbar: boolean,
    laedt: boolean,
    ausstehend: number,
    konflikte: number,
    fehlgeschlagen: number,
    downloadFehler: boolean
  ): ProjektOfflineZustand {
    if (laedt) {
      return 'laedt';
    }

    if (konflikte > 0) {
      return 'konflikt';
    }

    if (fehlgeschlagen > 0 || downloadFehler) {
      return 'fehler';
    }

    if (!verfuegbar) {
      return 'nicht_heruntergeladen';
    }

    if (ausstehend > 0) {
      return this.sync.laeuftGerade ? 'synchronisiert' : 'ausstehend';
    }

    return 'verfuegbar';
  }

  private leererStatus(projektId: number): OfflineProjektStatus {
    return {
      projektId,
      zustand: 'nicht_heruntergeladen',
      verfuegbar: false,
      ausstehend: 0,
      konflikte: 0,
      fehlgeschlagen: 0,
      schemaVeraltet: false,
      listen: 0,
      spsListen: 0
    };
  }

  private setzeFortschritt(projektId: number, schritt: string, erledigt: number, gesamt: number): void {
    this.laufend.set(projektId, { schritt, erledigt, gesamt });
    void this.aktualisiereStatus();
  }

  private async lokaleListenIds(projektId: number): Promise<Set<number>> {
    const projekt = await this.db.ladeProjekt(projektId);
    const listen = await this.db.ladeListenVonProjekt(projektId);

    return new Set<number>([...(projekt?.listenIds ?? []), ...listen.map(l => l.id)]);
  }

  private async zaehleOffen(projektId: number, listenIds: Set<number>): Promise<number> {
    const warteschlange = await this.db.ladeWarteschlange();
    const konflikte = await this.db.ladeKonflikte();

    return warteschlange.filter(e => this.db.gehoertZuProjekt(e, projektId, listenIds)).length
      + konflikte.filter(k => this.db.gehoertZuProjekt(k, projektId, listenIds)).length;
  }
}
